import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await apiClient.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await apiClient.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await apiClient.post('/auth/register', data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete apiClient.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};

// API Hooks
export const useApi = () => {
  const { token } = useAuth();

  useEffect(() => {
    if (token) {
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [token]);

  // Dashboard
  const getDashboardKPIs = useCallback(async () => {
    const response = await apiClient.get('/dashboard/kpis');
    return response.data;
  }, []);

  const getMapaInstituicoes = useCallback(async () => {
    const response = await apiClient.get('/dashboard/mapa-instituicoes');
    return response.data;
  }, []);

  // Distritos
  const getDistritos = useCallback(async () => {
    const response = await apiClient.get('/distritos');
    return response.data;
  }, []);

  // Valencias
  const getValencias = useCallback(async (categoria) => {
    const params = categoria ? { categoria } : {};
    const response = await apiClient.get('/valencias', { params });
    return response.data;
  }, []);

  const getValenciaCategorias = useCallback(async () => {
    const response = await apiClient.get('/valencias/categorias');
    return response.data;
  }, []);

  // Instituições
  const getInstituicoes = useCallback(async (filters = {}) => {
    const response = await apiClient.get('/instituicoes', { params: filters });
    return response.data;
  }, []);

  const getInstituicao = useCallback(async (id) => {
    const response = await apiClient.get(`/instituicoes/${id}`);
    return response.data;
  }, []);

  const createInstituicao = useCallback(async (data) => {
    const response = await apiClient.post('/instituicoes', data);
    return response.data;
  }, []);

  const updateInstituicao = useCallback(async (id, data) => {
    const response = await apiClient.put(`/instituicoes/${id}`, data);
    return response.data;
  }, []);

  const deleteInstituicao = useCallback(async (id) => {
    const response = await apiClient.delete(`/instituicoes/${id}`);
    return response.data;
  }, []);

  const getInstituicaoDadosRelacionados = useCallback(async (id) => {
    const response = await apiClient.get(`/instituicoes/${id}/dados-relacionados`);
    return response.data;
  }, []);

  const updateInstituicaoTipoMembro = useCallback(async (id, tipoMembro) => {
    const response = await apiClient.put(`/instituicoes/${id}/tipo-membro?tipo_membro=${tipoMembro}`);
    return response.data;
  }, []);

  const updateInstituicaoEstado = useCallback(async (id, estado, motivo) => {
    const response = await apiClient.put(`/instituicoes/${id}/estado`, { estado, motivo });
    return response.data;
  }, []);

  const enviarConviteCandidatura = useCallback(async (id) => {
    const response = await apiClient.post(`/instituicoes/${id}/enviar-convite-candidatura`);
    return response.data;
  }, []);

  // Utentes Agregados
  const getUtentesAgregados = useCallback(async (filters = {}) => {
    const response = await apiClient.get('/utentes-agregados', { params: filters });
    return response.data;
  }, []);

  const createUtentesAgregados = useCallback(async (data) => {
    const response = await apiClient.post('/utentes-agregados', data);
    return response.data;
  }, []);

  // Alertas
  const getAlertas = useCallback(async (filters = {}) => {
    const response = await apiClient.get('/alertas', { params: filters });
    return response.data;
  }, []);

  const marcarAlertaLida = useCallback(async (id) => {
    const response = await apiClient.put(`/alertas/${id}/marcar-lida`);
    return response.data;
  }, []);

  const deleteAlerta = useCallback(async (id) => {
    const response = await apiClient.delete(`/alertas/${id}`);
    return response.data;
  }, []);

  // Statistics
  const getTiposEntidade = useCallback(async () => {
    const response = await apiClient.get('/estatisticas/tipos-entidade');
    return response.data;
  }, []);

  const getSituacaoFinanceira = useCallback(async () => {
    const response = await apiClient.get('/estatisticas/situacao-financeira');
    return response.data;
  }, []);

  // Roles
  const getRoles = useCallback(async () => {
    const response = await apiClient.get('/roles');
    return response.data;
  }, []);

  return {
    getDashboardKPIs,
    getMapaInstituicoes,
    getDistritos,
    getValencias,
    getValenciaCategorias,
    getInstituicoes,
    getInstituicao,
    createInstituicao,
    updateInstituicao,
    deleteInstituicao,
    getInstituicaoDadosRelacionados,
    updateInstituicaoTipoMembro,
    updateInstituicaoEstado,
    enviarConviteCandidatura,
    getUtentesAgregados,
    createUtentesAgregados,
    getAlertas,
    marcarAlertaLida,
    deleteAlerta,
    getTiposEntidade,
    getSituacaoFinanceira,
    getRoles,
  };
};

export { apiClient, API };
